import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const handoff = params.get("handoff");
    const boot = async () => {
      // One-time app→browser handoff: ?handoff=TOKEN signs the browser in
      // so Android app users land on web checkout already authenticated.
      if (handoff) {
        try {
          const r = await api.post("/auth/handoff/redeem", { token: handoff });
          localStorage.setItem("spark_token", r.data.token);
          setUser(r.data.user);
          params.delete("handoff");
          const qs = params.toString();
          window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
          setLoading(false);
          return;
        } catch { /* expired/used token — fall through to normal boot */ }
      }
      const token = localStorage.getItem("spark_token");
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const r = await api.get("/auth/me");
        setUser(r.data);
      } catch {
        localStorage.removeItem("spark_token");
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("spark_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  // Passwordless sign-in used by the public /demo?as=<slug> route. Hits a
  // dedicated backend endpoint that returns a JWT for the requested demo
  // persona (self-heals seed data if the DB is empty). Never carries a
  // password on the wire — so a password/bcrypt rotation on demo users
  // can never break shareable demo links (Google Play testers, etc.).
  const loginDemo = async (slug) => {
    const r = await api.post(`/demo/token?profile=${encodeURIComponent(slug)}`);
    localStorage.setItem("spark_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    localStorage.setItem("spark_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem("spark_token");
    setUser(null);
  };

  const refresh = async () => {
    const r = await api.get("/auth/me");
    setUser(r.data);
    return r.data;
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, loginDemo, register, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
